import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  SetCompanyFeatureFunctionalitiesDto,
  UserProfileDto,
  UserRole,
} from "@green0meter/api-client";

import { DOMAIN_COMPANIES } from "../domain";
import { ACTION_GET_ADMIN_COMPANIES } from "../GetAdminCompanies/useGetAdminCompanies";

import { companiesAdminApi } from "@api/services";
import { roleGuard } from "@api";

export const ACTION_SET_COMPANY_FEATURE_FUNCTIONALITIES =
  "SET_COMPANY_FEATURE_FUNCTIONALITIES";

const allowedRoles = [UserRole.Admin];

interface SetCompanyFeatureFunctionalitiesRequest {
  companyId: string;
  data: SetCompanyFeatureFunctionalitiesDto;
}

const setCompanyFeatureFunctionalities = async (
  params: SetCompanyFeatureFunctionalitiesRequest,
): Promise<void> => {
  await companiesAdminApi.companiesAdminControllerSetCompanyFeatureFunctionalities(
    params.companyId,
    params.data,
  );
};

export const useSetCompanyFeatureFunctionalities = (
  user: UserProfileDto | null,
): {
  setCompanyFeatureFunctionalities: (
    params: SetCompanyFeatureFunctionalitiesRequest,
  ) => void;
  setCompanyFeatureFunctionalitiesAsync: (
    params: SetCompanyFeatureFunctionalitiesRequest,
  ) => Promise<void>;
  setCompanyFeatureFunctionalitiesEnabled: boolean;
  isSuccessSetCompanyFeatureFunctionalities: boolean;
  isPendingSetCompanyFeatureFunctionalities: boolean;
} => {
  const enabled = roleGuard(user, allowedRoles);
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isSuccess, isPending } = useMutation({
    mutationKey: [DOMAIN_COMPANIES, ACTION_SET_COMPANY_FEATURE_FUNCTIONALITIES],
    mutationFn: enabled ? setCompanyFeatureFunctionalities : undefined,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [DOMAIN_COMPANIES, ACTION_GET_ADMIN_COMPANIES],
      });
    },
  });

  return {
    setCompanyFeatureFunctionalities: mutate,
    setCompanyFeatureFunctionalitiesAsync: mutateAsync,
    setCompanyFeatureFunctionalitiesEnabled: enabled,
    isSuccessSetCompanyFeatureFunctionalities: isSuccess,
    isPendingSetCompanyFeatureFunctionalities: isPending,
  };
};
